// frontend/src/components/engineer/EngineerSidebar.tsx
// UPDATED WITH DUTY RESUME MENU ITEM FOR LEAVE APPLICATION ACCESS
// PHOENICIAN TECHNICAL SERVICES LLC

import React, { useState, useCallback, useMemo } from 'react';
import {
  Squares2X2Icon,
  CogIcon,
  ClockIcon,
  DocumentChartBarIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  MoonIcon,
  UserGroupIcon,
  ClipboardDocumentListIcon,
  CalendarDaysIcon,        // 🆕 For Annual Leave
  ClipboardDocumentCheckIcon // 🆕 For Duty Resume
} from '@heroicons/react/24/outline';
import styles from './EngineerSidebar.module.css';

interface EngineerSidebarProps {
  activePage: string;
  onPageChange: (pageId: string) => void;
  isTransitioning?: boolean;
  onToggle?: (collapsed: boolean) => void;
  user?: any;
}

const EngineerSidebar: React.FC<EngineerSidebarProps> = React.memo(({ 
  activePage, 
  onPageChange, 
  isTransitioning = false,
  onToggle,
  user
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Menu items with conditional features based on user access
  const menuItems = useMemo(() => {
    const items = [
      {
        id: 'dashboard',
        label: 'Dashboard',
        icon: <Squares2X2Icon className={styles.heroIcon} />,
        isImage: false,
        hasSubmenu: false
      }
    ];

    // Show Manpower tab if user has manpowerAccess
    if (user?.manpowerAccess) {
      items.push({
        id: 'manpower',
        label: 'Manpower',
        icon: <UserGroupIcon className={styles.heroIcon} />,
        isImage: false,
        hasSubmenu: false
      });
    }

    // Show Leave Management items if user has leaveApplicationAccess
    if (user?.leaveApplicationAccess) {
      items.push(
        {
          id: 'apply-leave',
          label: 'Apply Leave',
          icon: <ClipboardDocumentListIcon className={styles.heroIcon} />,
          isImage: false,
          hasSubmenu: false
        },
        {
          id: 'apply-annual-leave',
          label: 'Annual Leave',
          icon: <CalendarDaysIcon className={styles.heroIcon} />,
          isImage: false,
          hasSubmenu: false
        },
        {
          id: 'duty-resume',
          label: 'Duty Resume',
          icon: <ClipboardDocumentCheckIcon className={styles.heroIcon} />,
          isImage: false,
          hasSubmenu: false
        },
        {
          id: 'leave-reports',
          label: 'Leave Reports',
          icon: <DocumentChartBarIcon className={styles.heroIcon} />,
          isImage: false,
          hasSubmenu: false
        }
      );
    }

    // Only show overtime features if user has access
    if (user?.hasOvertimeAccess) {
      items.push(
        {
          id: 'create-overtime',
          label: 'Create Overtime',
          icon: <ClockIcon className={styles.heroIcon} />,
          isImage: false,
          hasSubmenu: false
        },
        {
          id: 'no-break',
          label: 'No Break',
          icon: <ExclamationTriangleIcon className={styles.heroIcon} />,
          isImage: false,
          hasSubmenu: false
        },
        {
          id: 'reports-history',
          label: 'Reports & History',
          icon: <DocumentChartBarIcon className={styles.heroIcon} />,
          isImage: false,
          hasSubmenu: false
        },
        {
          id: 'view-timesheets',
          label: 'View Timesheets',
          icon: <DocumentTextIcon className={styles.heroIcon} />,
          isImage: false,
          hasSubmenu: false
        },
        {
          id: 'night-shift-workers',
          label: 'Night Shift Workers',
          icon: <MoonIcon className={styles.heroIcon} />,
          isImage: false,
          hasSubmenu: false
        }
      );
    }

    return items;
  }, [user?.hasOvertimeAccess, user?.manpowerAccess, user?.leaveApplicationAccess]);

  const toggleSidebar = useCallback(() => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);
    
    if (onToggle) {
      onToggle(newCollapsedState);
    }
  }, [isCollapsed, onToggle]);

  const handleItemClick = useCallback((itemId: string) => {
    if (isTransitioning) {
      return;
    }

    if (activePage !== itemId) {
      onPageChange(itemId);
    }
  }, [activePage, onPageChange, isTransitioning]);

  const isItemActive = useCallback((item: any) => {
    return activePage === item.id;
  }, [activePage]);

  return (
    <aside className={`${styles.engineerSidebar} ${isCollapsed ? styles.collapsed : ''} ${isTransitioning ? styles.transitioning : ''}`}>
      <div className={styles.sidebarHeader}>
        <button 
          className={styles.toggleButton}
          onClick={toggleSidebar}
          aria-label="Toggle sidebar"
          disabled={isTransitioning}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <line x1="3" y1="6" x2="21" y2="6" stroke="currentColor" strokeWidth="2"/>
            <line x1="3" y1="12" x2="21" y2="12" stroke="currentColor" strokeWidth="2"/>
            <line x1="3" y1="18" x2="21" y2="18" stroke="currentColor" strokeWidth="2"/>
          </svg>
        </button>
      </div>

      <nav className={styles.nav}>
        <ul className={styles.navList}>
          {menuItems.map((item) => {
            const isActive = isItemActive(item);
            
            return (
              <li key={item.id}>
                <button
                  className={`
                    ${styles.navItem} 
                    ${isActive ? styles.active : ''} 
                    ${isTransitioning ? styles.transitioning : ''}
                  `.trim()}
                  aria-label={item.label}
                  onClick={() => handleItemClick(item.id)}
                  disabled={isTransitioning}
                >
                  <span className={styles.navIcon}>
                    {item.icon}
                  </span>
                  <span className={styles.navLabel}>{item.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Engineer info footer */}
      {!isCollapsed && user && (
        <div className={styles.sidebarFooter}>
          <div className={styles.userInfo}>
            <div className={styles.userAvatar}>
              <div className={styles.avatarCircle}>
                {user.name ? user.name.charAt(0).toUpperCase() : 'E'}
              </div>
            </div>
            <div className={styles.userDetails}>
              <div className={styles.userName}>{user.name || 'Engineer'}</div>
              <div className={styles.userRole}>{user.designation || 'Engineer'}</div>
              <div className={styles.userDepartment}>{user.department || 'Engineering'}</div>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
});

EngineerSidebar.displayName = 'EngineerSidebar';
export default EngineerSidebar;